import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../lib/api';
import PhoneInput from './PhoneInput';

export default function Login() {
  const navigate = useNavigate();
  const signIn = useMutation(api.auth.signIn);
  const requestMagicLink = useMutation(api.auth.requestMagicLink);

  // Get saved country code preference or default to Mexico
  const getDefaultCountryCode = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('preferredCountryCode') || '+52';
    }
    return '+52';
  };

  const [formData, setFormData] = useState({
    phoneNumber: '',
    password: '',
    countryCode: getDefaultCountryCode(),
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [devMagicLink, setDevMagicLink] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const fullPhone = formData.countryCode + formData.phoneNumber.replace(/\D/g, '');
      
      const result = await signIn({
        phoneNumber: fullPhone,
        password: formData.password,
      });

      // Store user data in localStorage
      localStorage.setItem('playerId', result.playerId);
      localStorage.setItem('playerName', result.name);
      localStorage.setItem('playerRole', result.role);

      // Navigate based on role
      if (result.role === 'organizer') {
        navigate('/dashboard/organizer');
      } else if (result.role === 'administrator') {
        navigate('/dashboard/admin');
      } else {
        navigate('/dashboard/player');
      }
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    setError('');
    setLoading(true);
    setMagicLinkSent(false);

    try {
      const fullPhone = formData.countryCode + formData.phoneNumber.replace(/\D/g, '');

      const result = await requestMagicLink({
        phoneNumber: fullPhone,
      });

      setMagicLinkSent(true);

      // In development, show the magic link
      if (result.devMagicLink) {
        setDevMagicLink(result.devMagicLink);
      }
    } catch (err: any) {
      setError(err.message || 'Error al enviar el enlace');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img 
            src="/torneopadel.png" 
            alt="TorneoPadel" 
            className="w-32 h-32 mx-auto mb-4 object-contain"
          />
          <h2 className="text-3xl font-bold text-gray-900">Iniciar Sesión</h2>
          <p className="mt-2 text-gray-600">
            Bienvenido de vuelta
          </p>
        </div>

        {magicLinkSent ? (
          <div className="mt-8 space-y-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-green-800 mb-2">
                ¡Enlace enviado por WhatsApp! 📱
              </h3>
              <p className="text-sm text-green-700">
                Revisa tu WhatsApp y haz clic en el enlace para iniciar sesión.
              </p>
              <p className="text-xs text-green-600 mt-2">
                El enlace expirará en 15 minutos.
              </p>
            </div>

            {/* Development only - show magic link */}
            {devMagicLink && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-yellow-800 mb-2">
                  Enlace de desarrollo (solo visible en desarrollo)
                </h3>
                <div className="text-xs text-yellow-700 break-all">
                  <p>{devMagicLink}</p>
                  <Link
                    to={devMagicLink.replace(window.location.origin, '')}
                    className="mt-2 inline-block text-yellow-800 underline font-medium"
                  >
                    Ir al enlace de acceso
                  </Link>
                </div>
              </div>
            )}

            <button
              onClick={() => {
                setMagicLinkSent(false);
                setDevMagicLink('');
                setShowMagicLink(false);
              }}
              className="w-full text-sm text-gray-600 hover:text-gray-900"
            >
              ← Volver al inicio de sesión
            </button>
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            {error && (
              <div className="bg-red-50 text-red-500 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {!showMagicLink ? (
              <>
                <form onSubmit={handleLogin} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Número de Teléfono
                    </label>
                    <PhoneInput
                      countryCode={formData.countryCode}
                      phoneNumber={formData.phoneNumber}
                      onCountryCodeChange={(code) => setFormData({ ...formData, countryCode: code })}
                      onPhoneNumberChange={(number) => setFormData({ ...formData, phoneNumber: number })}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contraseña
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Tu contraseña"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                  </button>
                </form>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gray-50 text-gray-500">O</span>
                  </div>
                </div>

                <button
                  onClick={() => setShowMagicLink(true)}
                  className="w-full py-3 px-4 border border-green-300 rounded-lg shadow-sm text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 font-medium flex items-center justify-center"
                >
                  <span className="mr-2">📱</span>
                  Iniciar sesión con WhatsApp
                </button>
              </>
            ) : (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número de Teléfono
                  </label>
                  <PhoneInput
                    countryCode={formData.countryCode}
                    phoneNumber={formData.phoneNumber}
                    onCountryCodeChange={(code) => setFormData({ ...formData, countryCode: code })}
                    onPhoneNumberChange={(number) => setFormData({ ...formData, phoneNumber: number })}
                    required
                  />
                </div>

                <button
                  onClick={handleMagicLink}
                  disabled={loading || !formData.phoneNumber}
                  className="w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {loading ? 'Enviando enlace...' : 'Enviar enlace por WhatsApp'}
                </button>

                <button
                  onClick={() => setShowMagicLink(false)}
                  className="w-full text-sm text-gray-600 hover:text-gray-900"
                >
                  ← Usar contraseña en su lugar
                </button>
              </div>
            )}
          </div>
        )}

          <div className="flex flex-col items-center space-y-2">
            <Link to="/reset-password" className="text-sm font-medium text-blue-600 hover:text-blue-500">
              ¿Olvidaste tu contraseña?
            </Link>
            <span className="text-sm text-gray-600">
              ¿No tienes cuenta?{' '}
              <Link to="/signup" className="font-medium text-blue-600 hover:text-blue-500">
                Registrarse
              </Link>
            </span>
          </div>
      </div>
    </div>
  );
}